import React, { useState } from 'react';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useScrollProgress } from '../hooks/useScrollProgress';

const Navbar = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const scrollYProgress = useScrollProgress();
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, 'change', (v) => setHasScrolled(v > 80));

  const scrollSections = [
    { id: 'home', label: 'HOME' },
    { id: 'projects', label: 'PROJECTS' },
    { id: 'skills', label: 'STACK' },
  ];

  const renderNavLink = (section: string) => {
    const isActive = location.hash === `#${section}` || (!location.hash && section === 'home');
    const linkClasses = `text-white cursor-pointer hover:text-[#00c8ff] transition-colors relative group tracking-wider
      ${isActive ? 'text-[#00c8ff]' : ''}`;
    
    if (isHomePage) {
      return (
        <ScrollLink
          to={section}
          spy={true}
          smooth={true}
          duration={500}
          className={linkClasses}
          activeClass="text-[#00c8ff]"
        >
          {section.toUpperCase()}
          <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#00c8ff] transition-all group-hover:w-full"></span>
        </ScrollLink>
      );
    } else {
      return (
        <RouterLink
          to={`/#${section}`}
          className={linkClasses}
        >
          {section.toUpperCase()}
          <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#00c8ff] transition-all group-hover:w-full"></span>
        </RouterLink>
      );
    }
  };

  return (
    <nav
      className={`fixed w-full z-50 h-16 transition-all duration-200 border-b ${
        hasScrolled ? 'bg-black/90 backdrop-blur-xl border-white/10' : 'bg-black/80 backdrop-blur-xl border-white/5'
      }`}
    >
      {/* Scroll progress bar */}
      <motion.div
        className="absolute bottom-0 left-0 h-0.5 bg-[#00c8ff] origin-left"
        style={{ scaleX: scrollYProgress }}
      />
      <div className="max-w-7xl mx-auto px-6 h-full flex justify-between items-center">
        <RouterLink
          to="/"
          className="font-sans text-base font-semibold text-white whitespace-nowrap"
        >
          Sebastien Toscano
        </RouterLink>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white focus:outline-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Desktop Menu */}
        <ul className="hidden md:flex space-x-8 text-sm font-medium">
          {scrollSections.map((section) => (
            <li key={section.id}>
              {renderNavLink(section.id)}
            </li>
          ))}
          <li>
            {renderNavLink('contact')}
          </li>
        </ul>

        {/* Mobile Menu */}
        <div className={`md:hidden absolute top-16 left-0 w-full bg-black/95 backdrop-blur-md transition-all duration-300 ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
          <ul className="flex flex-col space-y-4 p-6">
            {scrollSections.map((section) => (
              <li key={section.id} onClick={() => setIsMenuOpen(false)}>
                {renderNavLink(section.id)}
              </li>
            ))}
            <li onClick={() => setIsMenuOpen(false)}>
              {renderNavLink('contact')}
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
